import { useState, useEffect } from "react";
import { saCellphoneFormRule } from "../../../utils/systemValidation";
import {
  Button,
  Input,
  InputNumber,
  Form,
  Select,
  Space,
  Row,
  Col,
  Card,
  DatePicker,
  Skeleton,
  message,
} from "antd";
import {
  StopOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { SelectProps } from "antd/lib/select";

import {
  addNutunDebiCheckPaymentApi,
  editDebiCheckPaymentApi,
  removePaymentApi,
} from "../../../services/Payment/PaymentApis";
import { getMemberApi } from "../../../services/Member/MemberApis";
import { mandateCheck } from "../../../utils/debiCheckHelper";

interface StepProps {
  buttonLoading: boolean;
  gotoStep: Function;
  form: any;
  update: boolean;
  submit: Function;
  memberId: any;
}

interface CardData {
  id: number;
  Type: string;
  Bank: string;
  AccountNumber: string;
  AccountType: string;
  BranchCode: string;
  StartDate: string;
  FirstCollectionDate?: string | null;
  FirstCollectionAmount?: string | null;
  MandateId?: string | number | null;
  MandateInstructionId?: string | number | null;
  PaymentDate: string;
  Cellphone: string;
  Email: string;
  IdNumber: string;
  Name: string;
  Amount: string;
  PaymentFrequency: string;
  MandateStatus: string;
  MandateInstructionStatus: string;
  MandateAccepted: boolean;
  MandateProcessed: boolean;
}

interface FormData {
  Type: string;
  Bank: string;
  AccountNumber: string;
  AccountType: string;
  BranchCode: string;
  PaymentDate: string;
  Cellphone: string;
  Email: string;
  IdNumber: string;
  Name: string;
  Amount: string;
  PaymentFrequency: string;
}

const bank: SelectProps["options"] = [
  {
    label: "ABSA",
    value: "ABSA",
  },
  {
    label: "Access",
    value: "Access",
  },
  {
    label: "African Bank",
    value: "African Bank",
  },
  {
    label: "Bidvest Bank",
    value: "Bidvest Bank",
  },
  {
    label: "Capitec",
    value: "Capitec",
  },
  {
    label: "FinBond",
    value: "FinBond",
  },
  {
    label: "FNB",
    value: "FNB",
  },
  {
    label: "Nedbank",
    value: "Nedbank",
  },
  {
    label: "Standard Bank",
    value: "Standard Bank",
  },
  {
    label: "TymeBank",
    value: "TymeBank",
  },
];

const payment: SelectProps["options"] = [
  {
    name: "Type",
    label: "Debi-Check",
    value: "DebiCheck",
  },
  {
    name: "Type",
    label: "DebitOrder",
    value: "DebitOrder",
  },
];

const account: SelectProps["options"] = [
  {
    name: "AccountType",
    label: "Savings",
    value: "Savings",
  },
  {
    name: "AccountType",
    label: "Current",
    value: "Current",
  },
];

const frequencyOpt: SelectProps["options"] = [
  {
    name: "Weekly",
    label: "Weekly",
    value: "Weekly",
  },
  {
    name: "Monthly",
    label: "Monthly",
    value: "Monthly",
  },
];

const tailLayout = {
  labelCol: { span: 8 },
  wrapperCol: { offset: 8, span: 14 },
};

const NutunPaymentInfo: React.FC<StepProps> = ({
  buttonLoading,
  gotoStep,
  form,
  update,
  submit,
  memberId,
}) => {
  const [card, setCard] = useState<CardData[]>([]);
  const [formData, setFormData] = useState<FormData>({
    Name: "",
    Cellphone: "",
    Email: "",
    IdNumber: "",
    Type: "DebiCheck",
    Bank: "",
    AccountNumber: "",
    AccountType: "",
    BranchCode: "",
    PaymentDate: "",
    Amount: "",
    PaymentFrequency: "",
  });
  // const [paymentId, setPaymentId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [processed, setProcessed] = useState<boolean>(false);
  const [disabled, setDisabled] = useState(update);
  const [member, setMember] = useState();

  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { id, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  };

  const handleInputNumberChange = (value: any) => {
    setFormData((prevData) => ({
      ...prevData,
      PaymentDate: value,
    }));
  };

  const handleSelectChange = (e: any, id: string) => {
    switch (id) {
      case "Bank": {
        setFormData((prevData) => ({
          ...prevData,
          Bank: e,
        }));
        break;
      }
      case "AccountType": {
        setFormData((prevData) => ({
          ...prevData,
          AccountType: e,
        }));
        break;
      }
      case "PaymentFrequency": {
        setFormData((prevData) => ({
          ...prevData,
          PaymentFrequency: e,
        }));
        break;
      }
    }
  };

  const handleDateChange = (id: string, value: any) => {
    switch (id) {
      case "StartDate": {
        setFormData((prevData) => ({
          ...prevData,
          StartDate: value.format("YYYY-MM-DD"),
        }));
        break;
      }
    }
  };

  const delay = (ms: any) => new Promise((resolve) => setTimeout(resolve, ms));

  const addMandate = async () => {
    setLoading(true);

    // Call mandate instruction
    const paymentApiResponse = await addNutunDebiCheckPaymentApi(memberId, formData);

    if (!paymentApiResponse.success) {
      setLoading(false);
      message.error(
        "Mandate could not be captured, please review information and try again."
      );
      throw new Error("Adding payment details failed.");
    }

    // If successful create card and set Mandate to Pending
    const newCard = {
      id: card.length + 1,
      ...paymentApiResponse.data,
    };

    setCard((prevCard) => [...prevCard, newCard]);

    const memberApiResponse = await getMemberApi(String(memberId));

    if (!memberApiResponse.success) {
      setLoading(false);
      message.error("Fetching member details failed.");
      throw new Error("Fetching member details failed.");
    }

    const paymentTypeId = memberApiResponse.data.PaymentType.id;

    // Run deibiCheckHelper Function
    const mandateResponse = await mandateCheck(
      `${process.env.REACT_APP_API_URL}/api/${process.env.REACT_APP_VERSION}/payment/${paymentTypeId}`
    );

    // If Function returns true update mandate status
    if (mandateResponse) {
      setLoading(false);
      setProcessed(true);
      message.success("Successfully loaded mandate.");
      // Continue checking for a response in the background
    } else {
      message.info("Mandate still being processed.");
      setLoading(false);

      // Removed Background check while integrating new API's
      // while (!processed) {
      //   const { data } = await getPaymentApi(paymentTypeId);
      //   if (data.MandateProcessed) {
      //     setProcessed(true);
      //   } else {
      //     await delay(10000);
      //   }
      // }
    }

    setFormData({
      Type: "",
      Bank: "",
      AccountNumber: "",
      AccountType: "",
      BranchCode: "",
      PaymentDate: "",
      Cellphone: "",
      Email: "",
      IdNumber: "",
      Name: "",
      Amount: "",
      PaymentFrequency: "",
    });
  };

  const updateMandate = async () => {
    // setLoading(true);

    // // Call mandate instruction
    const paymentApiResponse = await editDebiCheckPaymentApi(
      memberId,
      formData
    );

    if (!paymentApiResponse.success) {
      setLoading(false);
      message.error(
        "Mandate could not be captured, please review information and try again."
      );
      throw new Error("Adding payment details failed.");
    }

    // // If successful create card and set Mandate to Pending
    // const newCard = {
    //   id: card.length + 1,
    //   ...paymentApiResponse.data,
    // };

    // setCard((prevCard) => [...prevCard, newCard]);

    const memberApiResponse = await getMemberApi(String(memberId));

    if (!memberApiResponse.success) {
      setLoading(false);
      message.error("Fetching member details failed.");
      throw new Error("Fetching member details failed.");
    }

    const paymentTypeId = memberApiResponse.data.PaymentType.id;

    // // Run deibiCheckHelper Function
    const mandateResponse = await mandateCheck(
      `${process.env.REACT_APP_API_URL}/api/${process.env.REACT_APP_VERSION}/payment/${paymentTypeId}`
    );

    // // If Function returns true update mandate status
    if (mandateResponse) {
      setLoading(false);
      setProcessed(true);
      message.success("Successfully updated mandate.");
      // Continue checking for a response in the background
    } else {
      message.info("Mandate still being processed.");
      setLoading(false);
    }
    //   // Removed Background check while integrating new API's
    //   // while (!processed) {
    //   //   const { data } = await getPaymentApi(paymentTypeId);
    //   //   if (data.MandateProcessed) {
    //   //     setProcessed(true);
    //   //   } else {
    //   //     await delay(10000);
    //   //   }
    //   // }
    // }
  };

  const editMandate = async (card: CardData) => {
    setDisabled((prev: any) => !prev);
  };

  const removeMandate = async (card: CardData) => {
    await removePaymentApi(String(card.id));
    // setCard((prevCard) => prevCard.filter((c) => c.id !== card.id));
  };

  useEffect(() => {
    getMemberApi(String(memberId))
      .then((res: any) => {
        if (res.data.PaymentType != null) {
          setCard(() => [res.data.PaymentType]);
        }
        const initialFormData = form.getFieldsValue();

        form.setFieldsValue({
          Amount: res.data.TotalPremium,
          Type: "DebiCheck",
          PaymentFrequency: "Monthly",
        });

        setFormData((prevData) => ({
          ...prevData,
          ...initialFormData,
          Amount: res.data.TotalPremium,
          Type: "DebiCheck",
          PaymentFrequency: "Monthly",
        }));
        setMember(res.data);
      })
      .catch((err: any) => {
        console.log(err);
      });
  }, []);

  const content = (
    <>
      <Row>
        <Col span={16}>
          <Form.Item
            name="Name"
            label="Name"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input
              disabled={disabled}
              size="large"
              placeholder="Name"
              id="Name"
              onChange={handleInputChange}
            />
          </Form.Item>
          <Form.Item
            name="Surname"
            label="Surname"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input
              disabled={disabled}
              size="large"
              placeholder="Surname"
              id="Surname"
              onChange={handleInputChange}
            />
          </Form.Item>
          <Form.Item
            name="Cellphone"
            label="Cellphone"
            rules={[saCellphoneFormRule]}
          >
            <Input
              disabled={disabled}
              size="large"
              id="Cellphone"
              placeholder="Cellphone"
              onChange={handleInputChange}
            />
          </Form.Item>
          <Form.Item
            name="Email"
            label="Email"
            // rules={[
            //   {
            //     required: true,
            //   },
            // ]}
          >
            <Input
              disabled={disabled}
              size="large"
              id="Email"
              placeholder="Email"
              onChange={handleInputChange}
            />
          </Form.Item>

          <Form.Item
            name="IdNumber"
            label="ID Number"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input
              disabled={disabled}
              size="large"
              placeholder="ID Number"
              id="IdNumber"
              onChange={handleInputChange}
            />
          </Form.Item>
          {/* <Form.Item
            name="Type"
            label="Payment Type"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select
              disabled={disabled}
              id="Type"
              options={payment}
              size="large"
              style={{ width: "100%" }}
              placeholder="Payment Type"
              // onChange={(e) => handleSelectChange(e)}
            />
          </Form.Item> */}
          <Form.Item
            name="AccountNumber"
            label="Account Number"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input
              disabled={disabled}
              size="large"
              id="AccountNumber"
              placeholder="Account Number"
              onChange={handleInputChange}
            />
          </Form.Item>
          <Form.Item
            name="Bank"
            label="Bank"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select
              disabled={disabled}
              id="Bank"
              options={bank}
              size="large"
              style={{ width: "100%" }}
              placeholder="Bank"
              onChange={(e) => handleSelectChange(e, "Bank")}
            />
          </Form.Item>
          <Form.Item
            name="AccountType"
            label="Account Type"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select
              disabled={disabled}
              id="AccountType"
              onChange={(e) => handleSelectChange(e, "AccountType")}
              options={account}
              size="large"
              style={{ width: "100%" }}
              placeholder="Account Type"
            />
          </Form.Item>
          <Form.Item
            name="BranchCode"
            label="Branch Code"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input
              disabled={disabled}
              size="large"
              id="BranchCode"
              placeholder="Branch Code"
              onChange={handleInputChange}
            />
          </Form.Item>
          <Form.Item
            name="Amount"
            label="Payment Amount"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input
              disabled={true}
              size="large"
              id="Amount"
              placeholder="Payment Amount"
            />
          </Form.Item>
          <Form.Item
            name="PaymentDate"
            label="Payment Date"
            initialValue={formData.PaymentDate}
            extra="Day of month (1–31). If the month is shorter, collection falls on the last day of that month."
            rules={[
              {
                required: true,
              },
            ]}
          >
            <InputNumber
              disabled={disabled}
              size="large"
              min={1}
              max={31}
              onChange={handleInputNumberChange}
            />
          </Form.Item>

          {/* <Form.Item
            name="PaymentFrequency"
            label="Payment Frequency"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select
              disabled={true}
              options={frequencyOpt}
              style={{ width: "100%" }}
              size="large"
              placeholder="Select Payment Frequency"
              onChange={(e) => handleSelectChange(e, "PaymentFrequency")}
            />
          </Form.Item> */}
          {update ? (
            <Form.Item {...tailLayout}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button
                  disabled={disabled}
                  loading={loading}
                  style={{ marginBottom: 10 }}
                  block
                  size="large"
                  onClick={updateMandate}
                >
                  <span>Update</span>
                </Button>
              </Space>
            </Form.Item>
          ) : (
            <Form.Item {...tailLayout}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button
                  disabled={card.length > 0}
                  loading={loading}
                  style={{ marginBottom: 10 }}
                  block
                  size="large"
                  onClick={addMandate}
                >
                  <span>Add</span>
                </Button>
              </Space>
              {card.length > 0 && (
                <span style={{ color: "red" }}>
                  You cannot add another Mandate. Remove current Mandate before
                  adding a new one.
                </span>
              )}
            </Form.Item>
          )}

          <Form.Item {...tailLayout}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Button
                loading={buttonLoading}
                style={{ marginBottom: 10 }}
                block
                size="large"
                onClick={() => {
                  submit().then((result: any) => {
                    if (result) {
                      gotoStep(2);
                    }
                  });
                }}
              >
                Okay
              </Button>
            </Space>
          </Form.Item>
        </Col>

        <Col span={8}>
          <h3 style={{ marginBottom: "16px" }}>
            Current Debi-Check/DebitOrder Mandate
          </h3>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {!loading ? (
              card.map((card) => (
                <Card
                  key={card && card.id}
                  title={
                    <div>
                      {card && card.MandateAccepted ? (
                        <>
                          <span style={{ color: "Green" }}>
                            Mandate Accepted -{" "}
                          </span>
                          <CheckCircleOutlined style={{ color: "Green" }} />
                        </>
                      ) : card && card.MandateProcessed === false ? (
                        <>
                          <span style={{ color: "Blue" }}>
                            Mandate Pending -{" "}
                          </span>
                          <ClockCircleOutlined style={{ color: "Blue" }} />
                        </>
                      ) : card &&
                        card.MandateProcessed === true &&
                        card.MandateStatus !== "" ? (
                        <>
                          <span style={{ color: "Blue" }}>
                            Mandate {card.MandateStatus} -{" "}
                          </span>
                          <ClockCircleOutlined style={{ color: "Blue" }} />
                        </>
                      ) : (
                        <>
                          <span style={{ color: "Red" }}>
                            Mandate Rejected -{" "}
                          </span>
                          <StopOutlined style={{ color: "Red" }} />
                        </>
                      )}
                    </div>
                  }
                  style={{ marginBottom: 16 }}
                >
                  <div style={{ marginBottom: 16 }}>
                    <p>
                      Mandate ID -{" "}
                      <strong>
                        {card?.MandateInstructionId != null &&
                        card.MandateInstructionId !== ""
                          ? card.MandateInstructionId
                          : "N/A"}
                      </strong>
                    </p>
                    <p>
                      Mandate Instruction Status -{" "}
                      <strong>{card && card.MandateInstructionStatus}</strong>
                    </p>
                    <p>
                      First collection date -{" "}
                      <strong>
                        {card?.FirstCollectionDate
                          ? card.FirstCollectionDate
                          : "N/A"}
                      </strong>
                    </p>
                    <p>
                      First collection amount -{" "}
                      <strong>
                        {card?.FirstCollectionAmount != null &&
                        card.FirstCollectionAmount !== ""
                          ? `R ${card.FirstCollectionAmount}`
                          : "N/A"}
                      </strong>
                    </p>
                    <p>
                      Instalment date -{" "}
                      <strong>{card?.StartDate || "N/A"}</strong>
                    </p>
                    <p>
                      Instalment amount -{" "}
                      <strong>
                        {card?.Amount != null && card.Amount !== ""
                          ? `R ${card.Amount}`
                          : "N/A"}
                      </strong>
                    </p>
                    <p>
                      Payment Date - <strong>{card && card.PaymentDate}</strong>{" "}
                    </p>
                  </div>
                  {/* Removed these two buttons while awaiting on Nutun for integration */}
                  {/* <Button
                    type="primary"
                    onClick={() => editMandate(card)}
                    style={{ marginRight: 8 }}
                  >
                    Edit
                  </Button>
                  <Button
                    type="primary"
                    danger
                    onClick={() => removeMandate(card)}
                  >
                    Remove
                  </Button> */}
                </Card>
              ))
            ) : (
              <Card>
                <Skeleton active />
              </Card>
            )}
          </div>
        </Col>
      </Row>
    </>
  );

  return content;
};

export default NutunPaymentInfo;
